/**
 * Shared look for the formatting-toolbar icon buttons: flat and uniform — no shadow, no
 * border. The buttons abut (the row's gap is 0, so there is no dead strip between
 * adjacent hit targets); the air around an icon is this button's own padding, which is
 * why the cell is 36px around a 16px icon.
 */
export const TOOLBAR_BUTTON_CHROME =
  "relative h-10 w-full justify-center rounded-md text-gray-700 hover:bg-gray-200 [&>svg:first-of-type]:size-4"

/**
 * The "this control has more options" caret, tucked UNDER the icon rather than beside it.
 * Absolute, so it costs no width: every icon stays centered in its cell and the row keeps
 * one even horizontal rhythm whether or not a control has a caret. It sits in the bottom
 * 12px of the 40px cell, clear of the centered 16px icon above it.
 */
export const TOOLBAR_CHEVRON = "absolute bottom-0 left-1/2 size-3 -translate-x-1/2"

/**
 * Selected/active: the glyph takes the brand teal, with no fill behind it. A fill reads as
 * a second, competing box in a row of abutting buttons; color reads at a glance.
 *
 * Highlight and text color opt OUT (`ColorSplitControl`'s `activeAccent`) — the color they
 * applied is their active state, carried by the icon itself.
 *
 * `TOOLBAR_BUTTON_ACTIVE` is applied by callers that track active state with a plain
 * className (menu triggers, the link button); `TOOLBAR_TOGGLE_SELECTED` is the same look
 * driven off react-aria's `data-selected` for real `ToggleButton`s.
 */
export const TOOLBAR_BUTTON_ACTIVE = "text-brand-700"

export const TOOLBAR_TOGGLE_SELECTED = "selected:text-brand-700"

/**
 * The checked row of a block-type menu (heading, list). Same accent as an active toolbar
 * button rather than a checkmark column: the menus are single-select, so the row's own
 * glyph and label can carry the state. `aria-checked` still does the a11y work — this is
 * paint only.
 */
export const SELECTED_ROW = (isSelected: boolean) => (isSelected ? "text-brand-700" : "")

/**
 * Glyph chrome for a block-type menu row. One size for every row — the icons carry
 * different intrinsic sizes (18px for the list set, 16px for the headings), so without
 * this they arrive at whatever size their own artwork declares. The stroke rules pull
 * borrowed icons onto the 1px hairline the rest of the set uses; ours already bake it in,
 * so it is a no-op for them.
 */
export const MENU_ROW_GLYPH =
  "[&>svg]:size-4 [&>svg]:shrink-0 [&>svg_*]:[vector-effect:non-scaling-stroke] [&>svg_*]:[stroke-width:1px]"
